/**
 * N3 · Competência 4 (Grandezas e medidas) · task-model H1.22.K01
 *
 *   "Ampliar ou reduzir uma figura na malha quadriculada numa razão dada."
 *
 * O triângulo ABC está na malha; ao lado, o triângulo A′B′C′ começa como uma CÓPIA do
 * mesmo tamanho, com A′ já no lugar (fixo). O aluno arrasta B′ e C′ até que A′B′C′
 * seja a ampliação (ou a redução) de ABC na razão pedida, na mesma posição.
 *
 * Mede a ideia central da semelhança: ampliar é MULTIPLICAR todas as medidas pelo
 * mesmo número. Os desvios têm nome:
 *  - somar a mesma quantidade a todas as medidas (o raciocínio aditivo, o erro
 *    clássico da proporcionalidade);
 *  - multiplicar só as medidas de uma direção, esticando a figura;
 *  - usar outra razão — inclusive não mexer, razão 1;
 *  - acertar o tamanho e virar a figura.
 *
 * AB é sempre horizontal; C pode ficar sobre A ou deslocado, para que "multiplicar
 * as medidas" não se reduza a dois comprimentos alinhados com a malha.
 *
 * Crédito parcial (GPCM): metade por vértice no lugar.
 */

import type { Rng } from '../../core/rng.ts';
import { adjetivo, type Genero } from '../../core/pt.ts';
import { sampleWhere } from '../../core/sampling.ts';
import type { Vec2 } from '../../geom/vec.ts';
import type { Shape } from '../../render/scene.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  AMPL_ADITIVO,
  AMPL_INCOMPLETO,
  AMPL_ORIENTACAO,
  AMPL_RAZAO_ERRADA,
  AMPL_UMA_DIRECAO,
} from '../../schema/errors.ts';
import type { DragTarget, ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type Inline } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';
import {
  aneisDeRevisao,
  gradeQuadriculada,
  LEGENDA_ANEIS,
  mesmoPonto,
  passoTxt,
} from '../_comum/plano.ts';

/** Unidades de desenho por quadradinho, e o tamanho da malha. */
export const U = 30;
const W = 17;
const H = 10;
/** A figura original fica à esquerda desta coluna; a nova, à direita. */
const DIVISA = 7;

/**
 * Cada abertura termina num substantivo, e o "ampliado/reduzido" que vem depois
 * concorda com ELE. As três terminavam em gêneros diferentes — "a mesma figura",
 * "o triângulo ABC", "o mesmo canteiro" — e o adjetivo era fixo no feminino: saía
 * "o triângulo ABC ampliada". Agora cada abertura declara o gênero de onde termina.
 */
const CONTEXTOS: readonly { readonly texto: string; readonly g: Genero }[] = [
  {
    texto:
      'Uma designer desenhou, numa malha quadriculada, o triângulo ABC, que faz parte de um logotipo. Para outro material, ela vai desenhar a mesma figura',
    g: 'f',
  },
  {
    texto:
      'Numa aula de desenho, a professora pediu que cada aluno reproduzisse na malha o triângulo ABC',
    g: 'm',
  },
  {
    texto:
      'A planta de um parque mostra, numa malha quadriculada, o canteiro triangular ABC. Em outra planta, o mesmo canteiro vai ser desenhado',
    g: 'm',
  },
];

export interface Params {
  /** 2, 3 ou 1/2. */
  readonly k: number;
  readonly A: Vec2;
  /** B = A + (a, 0). */
  readonly a: number;
  /** C = A + (c, b). */
  readonly c: number;
  readonly b: number;
  /** Onde fica A′. */
  readonly A2: Vec2;
  readonly contexto: number;
}

const soma = (p: Vec2, dx: number, dy: number): Vec2 => ({ x: p.x + dx, y: p.y + dy });

function candidato(g: Rng): Omit<Params, 'contexto'> | null {
  const k = g.pick([2, 2, 3, 0.5]);
  const comprimentos = k === 0.5 ? [2, 4] : k === 3 ? [1, 2, 3] : [1, 2, 3, 4];
  const la = g.pick(comprimentos);
  const lb = g.pick(comprimentos.filter((x) => x !== la));
  const a = la * (g.bool() ? 1 : -1);
  const b = lb * (g.bool() ? 1 : -1);
  const c = k === 0.5 ? g.pick([0, 0, 2, -2]) : g.pick([0, 0, 1, -1, 2, -2]);
  const A = { x: g.int(1, DIVISA - 1), y: g.int(1, H - 1) };
  const original = [A, soma(A, a, 0), soma(A, c, b)];
  if (!original.every((p) => p.x >= 1 && p.x <= DIVISA - 1 && p.y >= 1 && p.y <= H - 1))
    return null;
  const A2 = { x: g.int(DIVISA + 1, W - 1), y: g.int(0, H) };
  // A resposta e a cópia de partida precisam caber do lado direito.
  const nova = [A2, soma(A2, k * a, 0), soma(A2, k * c, k * b), soma(A2, a, 0), soma(A2, c, b)];
  if (!nova.every((p) => p.x >= DIVISA + 1 && p.x <= W && p.y >= 0 && p.y <= H)) return null;
  return { k, A, a, c, b, A2 };
}

function drawParams(rng: Rng): Params {
  const geo = sampleWhere(rng.fork('figura'), candidato, (x) => x !== null, {
    label: 'figura',
    maxTries: 400,
  }) as Omit<Params, 'contexto'>;
  return { ...geo, contexto: rng.int(0, CONTEXTOS.length - 1) };
}

const razaoTxt = (k: number): string => (k === 0.5 ? '1/2' : String(k));

const naMalha = (pos: readonly Vec2[]): Vec2[] => pos.map((p) => ({ x: p.x / U, y: p.y / U }));

/** Os deslocamentos de A′ até B′ e até C′, em quadradinhos. */
function vetores(ms: readonly Vec2[]): { vb: Vec2; vc: Vec2 } {
  const [a2, b2, c2] = ms as readonly [Vec2, Vec2, Vec2];
  return { vb: { x: b2.x - a2.x, y: b2.y - a2.y }, vc: { x: c2.x - a2.x, y: c2.y - a2.y } };
}

const igualV = (u: Vec2, x: number, y: number): boolean =>
  Math.abs(u.x - x) < 1e-9 && Math.abs(u.y - y) < 1e-9;

function alvoDe(p: Params): DragTarget {
  const { k, a, b, c } = p;
  const esperadoB = soma(p.A2, k * a, 0);
  const esperadoC = soma(p.A2, k * c, k * b);
  const noLugar = (ms: readonly Vec2[], i: 1 | 2): boolean => {
    const m = ms[i];
    return m !== undefined && mesmoPonto(m, i === 1 ? esperadoB : esperadoC);
  };
  const certo = (ms: readonly Vec2[]): boolean => noLugar(ms, 1) && noLugar(ms, 2);

  // Somar a mesma quantidade t a cada medida não nula, com t = (k − 1) × a menor delas.
  const menor = Math.min(...[a, b, c].filter((x) => x !== 0).map(Math.abs));
  const t = (k - 1) * menor;
  const aditiva = (d: number): number => (d === 0 ? 0 : d + Math.sign(d) * t);
  const aditivoPossivel = [a, b, c].every((d) => d === 0 || Math.abs(d) + t > 0);

  return {
    describe: [
      txt(
        `Arraste B′ e C′ até que A′B′C′ seja a figura ABC ${k > 1 ? 'ampliada' : 'reduzida'} na razão ${razaoTxt(k)}, na mesma posição.`,
      ),
    ],
    correto: (pos) => certo(naMalha(pos)),
    diagnostics: [
      {
        error: AMPL_ORIENTACAO,
        test: (pos) => {
          const { vb, vc } = vetores(naMalha(pos));
          return (
            (igualV(vb, -k * a, 0) && igualV(vc, -k * c, k * b)) ||
            (igualV(vb, k * a, 0) && igualV(vc, k * c, -k * b)) ||
            (igualV(vb, -k * a, 0) && igualV(vc, -k * c, -k * b))
          );
        },
      },
      {
        error: AMPL_RAZAO_ERRADA,
        test: (pos) => {
          const { vb, vc } = vetores(naMalha(pos));
          const m = vb.x / a;
          return (
            m > 0 && Math.abs(m - k) > 1e-9 && igualV(vb, m * a, 0) && igualV(vc, m * c, m * b)
          );
        },
      },
      {
        error: AMPL_UMA_DIRECAO,
        test: (pos) => {
          const { vb, vc } = vetores(naMalha(pos));
          return (
            (igualV(vb, k * a, 0) && igualV(vc, k * c, b)) ||
            (igualV(vb, a, 0) && igualV(vc, c, k * b))
          );
        },
      },
      {
        error: AMPL_ADITIVO,
        test: (pos) => {
          if (!aditivoPossivel) return false;
          const { vb, vc } = vetores(naMalha(pos));
          return igualV(vb, aditiva(a), 0) && igualV(vc, aditiva(c), aditiva(b));
        },
      },
      {
        error: AMPL_INCOMPLETO,
        test: (pos) => noLugar(naMalha(pos), 1) || noLugar(naMalha(pos), 2),
      },
    ],
    partialCredit: [
      { id: 'b', label: 'B′ no lugar certo', weight: 0.5, test: (pos) => noLugar(naMalha(pos), 1) },
      { id: 'c', label: 'C′ no lugar certo', weight: 0.5, test: (pos) => noLugar(naMalha(pos), 2) },
    ],
    analyze(pos) {
      const { vb, vc } = vetores(naMalha(pos));
      const caminho = (v: Vec2): string =>
        v.x === 0 && v.y === 0 ? 'nenhum deslocamento' : passoTxt(v.x, v.y);
      const out: Inline[] = [
        txt(
          `Na figura ABC, de A até B: ${passoTxt(a, 0)}; de A até C: ${caminho({ x: c, y: b })}. `,
        ),
        txt(`Na razão ${razaoTxt(k)}, cada um desses números é multiplicado por ${razaoTxt(k)}.`),
      ];
      if (!certo(naMalha(pos))) {
        out.push(
          br,
          txt(`Na sua figura, de A′ até B′: ${caminho(vb)}; de A′ até C′: ${caminho(vc)}. `),
          txt(
            `O pedido era: de A′ até B′, ${passoTxt(k * a, 0)}; de A′ até C′, ${caminho({ x: k * c, y: k * b })}.`,
          ),
        );
      }
      return out;
    },
    reviewShapes(pos) {
      const ms = naMalha(pos);
      const esperados = [esperadoB, esperadoC];
      const certos = ([1, 2] as const).filter((i) => noLugar(ms, i));
      const errados = ([1, 2] as const).filter((i) => !noLugar(ms, i));
      return [
        {
          t: 'poly',
          pts: [p.A2, esperadoB, esperadoC].map((v) => ({ x: v.x * U, y: v.y * U })),
          closed: true,
          role: 'construction',
        },
        ...aneisDeRevisao(
          certos.map((i) => ms[i] as Vec2),
          errados.map((i) => ms[i] as Vec2),
          errados.map((i) => esperados[i - 1] as Vec2),
          U,
        ),
      ];
    },
    reviewLegend: [...LEGENDA_ANEIS, txt(' O contorno tracejado é o triângulo A′B′C′ esperado.')],
  };
}

export const ampliacao: ItemTemplate<Params, null> = {
  id: templateId('h1-22-K01-ampliacao'),
  version: 1,
  taskModel: taskModelCode('H1.22.K01'),
  skills: [skillCode('H1.22')],
  axis: 'II',
  level: 'N3',
  title: 'Ampliar ou reduzir uma figura na malha',
  responseModel: 'GPCM',
  difficultySources: ['steps.2'],
  params: drawParams,
  solve: () => null,
  buggy: [],
  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.c !== 0) fontes.push('steps.3plus');
    if (p.k === 0.5) fontes.push('non-round-numbers');
    return fontes;
  },
  build(p) {
    const tela = (v: Vec2): Vec2 => ({ x: v.x * U, y: v.y * U });
    const B = soma(p.A, p.a, 0);
    const C = soma(p.A, p.c, p.b);
    const amplia = p.k > 1;
    const backdrop: Shape[] = [
      gradeQuadriculada(0, W, 0, H, U),
      { t: 'poly', pts: [p.A, B, C].map(tela), closed: true, role: 'given', fill: 'given-fill' },
      ...[p.A, B, C].map((v, i): Shape => ({
        t: 'point',
        p: tela(v),
        role: 'given',
        name: 'ABC'[i] as string,
      })),
    ];
    const ctx = CONTEXTOS[p.contexto] as (typeof CONTEXTOS)[number];
    const pedido = amplia
      ? `${adjetivo('ampliado', ctx.g, 'sg')} na razão ${p.k}: cada medida fica ${p.k} vezes maior`
      : `${adjetivo('reduzido', ctx.g, 'sg')} na razão 1/2: cada medida fica com a metade do tamanho`;
    return {
      stem: [
        txt(`${ctx.texto} ${pedido}.`),
        br,
        txt(
          'Na malha, o vértice A′ já foi marcado, e o triângulo A′B′C′ começou como uma cópia de ABC, do mesmo tamanho.',
        ),
      ],
      comando: [
        txt('Arraste '),
        strong('B′ e C′'),
        txt(
          ` até que A′B′C′ seja o triângulo ABC ${amplia ? 'ampliado' : 'reduzido'} na razão ${razaoTxt(p.k)}, na mesma posição — sem girar nem virar a figura.`,
        ),
      ],
      stimuli: [],
      interaction: {
        kind: 'drag-points',
        descricao:
          `Malha quadriculada de ${W} por ${H} quadradinhos, sem numeração. À esquerda, o triângulo ABC; à direita, o ` +
          'triângulo A′B′C′, com A′ fixo e B′ e C′ arrastáveis, que encaixam nos cruzamentos da malha.',
        backdrop,
        viewBox: { x: -0.9 * U, y: -0.9 * U, w: (W + 1.8) * U, h: (H + 1.8) * U },
        pontos: [
          { nome: 'A′', inicial: tela(p.A2) },
          { nome: 'B′', inicial: tela(soma(p.A2, p.a, 0)) },
          { nome: 'C′', inicial: tela(soma(p.A2, p.c, p.b)) },
        ],
        ligacoes: [
          [0, 1],
          [1, 2],
          [2, 0],
        ],
        passo: U,
        fixos: [0],
        alvo: alvoDe(p),
      },
      dicas: [
        [
          txt(
            `Na razão ${razaoTxt(p.k)}, cada medida da figura é multiplicada por ${razaoTxt(p.k)} — inclusive os deslocamentos na malha.`,
          ),
        ],
        [txt('Conte quantos quadradinhos há de A até B. Quantos deve haver de A′ até B′?')],
        [
          txt(
            'Faça o mesmo com o caminho de A até C: quantos quadradinhos na horizontal e quantos na vertical? Multiplique cada um.',
          ),
        ],
      ],
      rationale: [
        strong('Resolução. '),
        txt(
          `${amplia ? 'Ampliar' : 'Reduzir'} na razão ${razaoTxt(p.k)} é multiplicar todas as medidas por ${razaoTxt(p.k)} — inclusive os deslocamentos na malha.`,
        ),
        br,
        txt(`De A até B: ${passoTxt(p.a, 0)}. Então, de A′ até B′: ${passoTxt(p.k * p.a, 0)}.`),
        br,
        txt(
          `De A até C: ${passoTxt(p.c, p.b)}. Então, de A′ até C′: ${passoTxt(p.k * p.c, p.k * p.b)}.`,
        ),
        br,
        txt(
          'Somar a mesma quantidade a todas as medidas não serve: a figura ficaria deformada, e não semelhante à original.',
        ),
      ],
      declared: p.k === 0.5 || p.c !== 0 ? 'media' : 'facil',
    };
  },
};
